import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, CalendarDays, BadgeCheck, Sparkles, ArrowRight, Palmtree } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import VerseOfWeek from "@/components/VerseOfWeek";
import TipOfTheDay from "@/components/TipOfTheDay";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-stone-500">{label}</div>
        <Icon className="w-4 h-4 text-stone-400" />
      </div>
      <div className="mt-4 font-display text-4xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ students: 0, active_subscriptions: 0, lessons: 0, upcoming_lessons: 0 });
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/dashboard/stats");
        setStats(s.data);
        const l = await api.get("/lessons");
        setLessons(l.data.slice(0, 5));
      } catch {}
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Dashboard</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Good to see you, {user?.name?.split(" ")[0] || "Teacher"}.
        </h1>
        <p className="mt-2 text-stone-500">Here's how the week is shaping up.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Students" value={stats.students} testid="stat-students" />
        <Stat icon={BadgeCheck} label="Active subs" value={stats.active_subscriptions} testid="stat-subs" />
        <Stat icon={CalendarDays} label="Lessons total" value={stats.lessons} testid="stat-lessons" />
        <Stat icon={CalendarDays} label="Upcoming" value={stats.upcoming_lessons} testid="stat-upcoming" />
      </div>

      {/* Tip of the day — AI-personalized to weakest subject × learning style */}
      <TipOfTheDay />

      {/* Verse-of-the-week card (renders only when teacher has Christian students + learning verses) */}
      <VerseOfWeek />

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold">Next lessons</h2>
            <Link to="/dashboard/schedule" className="text-sm text-amber-600 hover:underline" data-testid="overview-see-schedule">
              See schedule <ArrowRight className="w-3.5 h-3.5 inline" />
            </Link>
          </div>
          <div className="mt-4 divide-y divide-stone-100">
            {lessons.length === 0 && (
              <div className="text-stone-500 py-8 text-center" data-testid="overview-empty-lessons">
                No lessons yet. Add a student, then plan your first lesson.
              </div>
            )}
            {lessons.map((l) => (
              <div key={l.id} className="py-3 flex items-center gap-4">
                <div className="w-14 text-center">
                  {(() => { const [y, m, dd] = l.date.split("-").map(n => parseInt(n, 10)); const dt = new Date(y, m - 1, dd); return (<>
                    <div className="text-xs uppercase tracking-[0.18em] text-stone-500">{dt.toLocaleString("en-US", { month: "short" })}</div>
                    <div className="font-display text-2xl font-bold">{dt.getDate()}</div>
                  </>); })()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{l.title}</div>
                  <div className="text-sm text-stone-500 truncate">{l.subject} · {l.time || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-2xl p-6 shadow-sm grain relative">
          <Sparkles className="w-6 h-6 relative" />
          <h3 className="mt-4 font-display text-xl font-semibold relative">Ready to help</h3>
          <p className="mt-2 text-white/90 text-sm relative">
            Get 5 fresh resource picks per student, tuned to their learning style.
          </p>
          <Link to="/dashboard/ai-picks" data-testid="overview-cta-ai">
            <Button className="mt-6 rounded-full bg-white text-amber-600 hover:bg-stone-100 font-semibold">
              Try Resource picks
            </Button>
          </Link>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-emerald-50 grid place-items-center">
          <Palmtree className="w-6 h-6 text-emerald-600" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-semibold">Testing, tutors & scholarships</div>
          <div className="text-sm text-stone-500">WRAT-5 sites, tutor partners, curriculum, Florida scholarships — all in one place.</div>
        </div>
        <Link to="/dashboard/resources" data-testid="overview-cta-resources">
          <Button variant="outline" className="rounded-full border-2 border-stone-200">Open resources</Button>
        </Link>
      </div>
    </div>
  );
}
